import {
  IconCheck,
  IconEye,
  IconFunction,
  IconPlus,
  IconSettings,
  IconTrash,
  IconWand,
  IconX,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, Alert, Badge, Button, Col, Row, Form } from "react-bootstrap";
import { AVAILABLE_FUNCTIONS } from "../AgentsConstants";

const StepFunctions = memo(
  ({ register, watch, setValue, handleAIFill, onGetSchemas }) => {
    // Estado para controlar qual accordion está expandido
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [schemaUpdateTrigger, setSchemaUpdateTrigger] = useState(0);

    useEffect(() => {
      const subscription = watch((value, { name, type }) => {
        // ✅ Verificar se mudança afeta schemas
        if (
          name &&
          name.includes("functionSchemas") &&
          (name.includes(".enabled") ||
            name.includes(".required") ||
            name.includes(".customFields"))
        ) {
          console.log("🔍 Campo que afeta schema mudou:", name, value[name]);
          setSchemaUpdateTrigger((prev) => prev + 1);
        }
      });

      return () => subscription.unsubscribe();
    }, [watch]);

    // Função para gerar o schema baseado nas configurações
    const generateFunctionSchema = useCallback(
      (functionId) => {
        const func = AVAILABLE_FUNCTIONS.find((f) => f.id === functionId);
        if (!func) return null;

        const functionConfig = watch(`functionSchemas.${functionId}`) || {};
        const schema = {
          name: functionId,
          description: func.description,
          parameters: {
            type: "object",
            properties: {},
            required: [],
          },
        };

        // Adicionar campos habilitados ao schema
        func.fields.forEach((field) => {
          if (functionConfig.fields?.[field.id]?.enabled) {
            const fieldConfig = functionConfig.fields[field.id];

            schema.parameters.properties[field.id] = {
              type: field.type,
              description: field.description,
            };

            // Adicionar enum se aplicável
            if (field.type === "enum" && field.options) {
              schema.parameters.properties[field.id] = {
                type: "string", 
                enum: field.options,
                description: field.description,
              };
            }

            // Adicionar constraint para integer
            if (field.type === "integer" && field.id === "avaliacao_numerica") {
              schema.parameters.properties[field.id].minimum = 1;
              schema.parameters.properties[field.id].maximum = 5;
            }

            // Adicionar aos required se obrigatório
            if (fieldConfig.required) {
              schema.parameters.required.push(field.id);
            }
          }
        });

        // Adicionar campo metadata se existirem campos personalizados
        if (functionConfig.customFields && func.allowCustomFields) {
          const validCustomFields = functionConfig.customFields.filter(
            (customField) => customField.key && customField.value
          );

          if (validCustomFields.length > 0) {
            const metadataProperties = {};
            const metadataRequired = [];

            validCustomFields.forEach((customField) => {
              metadataProperties[customField.key] = {
                type: "string",
                description: customField.value,
              };

              if (customField.required) {
                metadataRequired.push(customField.key);
              }
            });

            schema.parameters.properties.metadata = {
              type: "object",
              description: "Campos personalizados de metadata",
              properties: metadataProperties,
              required: metadataRequired,
            };

            // Se algum campo personalizado for obrigatório, o metadata também é obrigatório
            if (metadataRequired.length > 0) {
              schema.parameters.required.push("metadata");
            }
          }
        }

        return schema;
      },
      [watch, AVAILABLE_FUNCTIONS]
    );

    // Função para preview do schema
    const [showSchemaPreview, setShowSchemaPreview] = useState(false);
    const allSchemas = useMemo(() => {
      console.log(
        "🔍 DEBUG - Regenerando schemas (trigger)...",
        schemaUpdateTrigger
      );

      const enabled = AVAILABLE_FUNCTIONS.filter((func) =>
        watch(`functionSchemas.${func.id}.enabled`)
      );

      return enabled
        .map((func) => generateFunctionSchema(func.id))
        .filter(Boolean);
    }, [generateFunctionSchema, AVAILABLE_FUNCTIONS, schemaUpdateTrigger]);

    // Função para gerar os schemas preparados para o backend
    const getGeneratedSchemas = useCallback(() => {
      return allSchemas;
    }, [allSchemas]);

    // Passar a função para o componente pai via useEffect
    useEffect(() => {
      if (onGetSchemas) {
        onGetSchemas(getGeneratedSchemas);
      }
    }, [onGetSchemas, getGeneratedSchemas]);

    // Handler para mudança de accordion
    const handleAccordionSelect = (eventKey) => {
      setActiveAccordion(activeAccordion === eventKey ? null : eventKey);
    };

    // Handler para switch de função
    const handleFunctionToggle = (functionId, enabled) => {
      setValue(`functionSchemas.${functionId}.enabled`, enabled);
      if (enabled) {
        setActiveAccordion(functionId);
        // Inicializar campos personalizados se não existirem
        if (!watch(`functionSchemas.${functionId}.customFields`)) {
          setValue(`functionSchemas.${functionId}.customFields`, []);
        }
      } else if (activeAccordion === functionId) {
        setActiveAccordion(null);
      }
    };

    // Funções para gerenciar campos personalizados
    const addCustomField = (functionId) => {
      const currentFields =
        watch(`functionSchemas.${functionId}.customFields`) || [];
      const newField = { key: "", value: "", required: false };
      setValue(`functionSchemas.${functionId}.customFields`, [
        ...currentFields,
        newField,
      ]);
    };

    const removeCustomField = (functionId, index) => {
      const currentFields =
        watch(`functionSchemas.${functionId}.customFields`) || [];
      const newFields = currentFields.filter((_, i) => i !== index);
      setValue(`functionSchemas.${functionId}.customFields`, newFields);
    };

    const updateCustomField = (functionId, index, field, value) => {
      const currentFields =
        watch(`functionSchemas.${functionId}.customFields`) || [];
      const newFields = [...currentFields];
      newFields[index] = { ...newFields[index], [field]: value };
      setValue(`functionSchemas.${functionId}.customFields`, newFields);
    };

    // Validação para campos personalizados
    const validateCustomField = (key, value) => {
      const errors = [];

      // Validar chave
      if (!key || key.trim() === "") {
        errors.push("Chave é obrigatória");
      } else if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key)) {
        errors.push(
          "Chave deve começar com letra ou _ e conter apenas letras, números e _"
        );
      }

      // Validar valor
      if (!value || value.trim() === "") {
        errors.push("Descrição é obrigatória");
      }

      return errors;
    };

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <IconFunction className="me-2 text-primary" size={24} />
            <h4 className="mb-0">Funções e Capacidades</h4>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => setShowSchemaPreview(!showSchemaPreview)}
            >
              <IconEye className="me-1" size={14} />
              {showSchemaPreview ? "Ocultar" : "Ver"} Schemas
            </Button>
          </div>
        </div>

        <Form.Group className="mb-4">
          <Form.Check
            type="switch"
            id="functionsEnabled"
            className="ml-4"
            label={
              <span>
                <strong>🎯 Habilitar Funções Inteligentes</strong>
                <br />
                <small className="text-muted">
                  Permite que seu agente execute ações automáticas como agendar,
                  cancelar e muito mais!
                </small>
              </span>
            }
            {...register("functionsEnabled")}
          />
        </Form.Group>

        {/* Schema Preview Modal */}
        {showSchemaPreview && (
          <Alert variant="info" className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">📋 Preview dos Schemas Gerados</h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowSchemaPreview(false)}
              >
                <IconX size={14} />
              </Button>
            </div>
            <pre
              style={{ fontSize: "11px", maxHeight: "300px", overflow: "auto" }}
            >
              {JSON.stringify(allSchemas, null, 2)}
            </pre>
          </Alert>
        )}

        {watch("functionsEnabled") && (
          <div>
            <Alert variant="success" className="mb-4">
              <h6>🚀 Construtor de Funções Inteligentes</h6>
              <p className="mb-2">
                Configure exatamente quais informações cada função deve coletar
                do cliente. É como montar um quebra-cabeça - escolha apenas as
                peças que você precisa!
              </p>
              <small className="text-muted">
                💡 <strong>Dica:</strong> Comece habilitando apenas as funções
                que você realmente usa. Você pode sempre adicionar mais depois.
              </small>
            </Alert>

            {/* Accordion para Funções */}
            <Accordion
              activeKey={activeAccordion}
              onSelect={handleAccordionSelect}
              className="mb-4"
            >
              {AVAILABLE_FUNCTIONS.map((func) => (
                <Accordion.Item eventKey={func.id} key={func.id}>
                  <Accordion.Header>
                    <div className="d-flex align-items-center justify-content-between w-100 me-3">
                      <div className="d-flex align-items-center">
                        <span className="me-3" style={{ fontSize: "20px" }}>
                          {func.icon}
                        </span>
                        <div>
                          <h6 className="mb-0">{func.name}</h6>
                          <small className="text-muted">
                            {func.description}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-5">
                        {watch(`functionSchemas.${func.id}.enabled`) && (
                          <Badge
                            bg="success"
                            className="d-flex align-items-center mt-1"
                          >
                            <IconCheck size={12} className="me-1" />
                            Ativa
                          </Badge>
                        )}
                        <Form.Check
                          type="switch"
                          id={`function-${func.id}`}
                          className="m-0 p-0"
                          label=""
                          {...register(`functionSchemas.${func.id}.enabled`)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFunctionToggle(func.id, e.target.checked);
                          }}
                        />
                      </div>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body>
                    {watch(`functionSchemas.${func.id}.enabled`) && (
                      <div>
                        {func.fields.length > 0 ? (
                          <div>
                            <div className="mb-3">
                              <small className="text-muted">
                                🎛️ <strong>Selecione os campos</strong> que o
                                agente deve coletar:
                              </small>
                            </div>

                            <Row>
                              {func.fields.map((field) => (
                                <Col md={6} key={field.id} className="mb-3">
                                  <div className="border rounded p-3 h-100 bg-light">
                                    <div className="d-flex align-items-start justify-content-between mb-2">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center mb-1">
                                          <Form.Check
                                            type="switch"
                                            id={`field-${func.id}-${field.id}`}
                                            className="ml-3"
                                            label={
                                              <strong>{field.label}</strong>
                                            }
                                            {...register(
                                              `functionSchemas.${func.id}.fields.${field.id}.enabled`
                                            )}
                                          />
                                          <Badge
                                            bg={
                                              field.type === "string"
                                                ? "info"
                                                : field.type === "boolean"
                                                ? "warning"
                                                : field.type === "enum"
                                                ? "success"
                                                : "secondary"
                                            }
                                            className="ms-2"
                                            style={{ fontSize: "10px" }}
                                          >
                                            {field.type}
                                          </Badge>
                                        </div>
                                        <small className="text-muted">
                                          {field.description}
                                        </small>

                                        {field.type === "enum" &&
                                          field.options && (
                                            <div className="mt-1">
                                              <small className="text-info">
                                                📝 Opções:{" "}
                                                {field.options.join(", ")}
                                              </small>
                                            </div>
                                          )}
                                      </div>
                                    </div>

                                    {watch(
                                      `functionSchemas.${func.id}.fields.${field.id}.enabled`
                                    ) && (
                                      <div className="mt-2 ps-3 border-start border-primary">
                                        <Form.Check
                                          type="checkbox"
                                          id={`required-${func.id}-${field.id}`}
                                          label={
                                            <span>
                                              <strong className="text-danger">
                                                Campo Obrigatório
                                              </strong>
                                              <br />
                                              <small className="text-muted">
                                                Se marcado, o agente SEMPRE
                                                pedirá esta informação
                                              </small>
                                            </span>
                                          }
                                          {...register(
                                            `functionSchemas.${func.id}.fields.${field.id}.required`
                                          )}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ) : (
                          <Alert variant="info" className="mb-3">
                            <small>
                              🎯 Esta função não possui campos predefinidos.
                            </small>
                          </Alert>
                        )}

                        {/* Seção de Campos Personalizados */}
                        {func.allowCustomFields && (
                          <div className="mt-4 pt-3 border-top">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">
                                🔧 Campos Personalizados (Metadata)
                              </h6>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => addCustomField(func.id)}
                              >
                                <IconPlus className="me-1" size={14} />
                                Adicionar Campo
                              </Button>
                            </div>

                            <Alert variant="warning" className="mb-3">
                              <small>
                                <strong>
                                  📋 Formato dos Campos Personalizados:
                                </strong>
                                <br />• <strong>Chave:</strong> nome_do_campo
                                (sem espaços, use _ para separar palavras)
                                <br />• <strong>Descrição:</strong> Descrição
                                clara do que o campo representa
                                <br />
                                <br />
                                <strong>Exemplos:</strong>
                                <br />• <code>area_atuacao</code> → "Segmento de
                                negócio do cliente"
                                <br />• <code>volume_atendimento</code> →
                                "Número aproximado de conversas/dia"
                                <br />• <code>fonte_leads</code> → "Principal
                                origem dos leads"
                              </small>
                            </Alert>

                            {watch(
                              `functionSchemas.${func.id}.customFields`
                            )?.map((customField, index) => {
                              const fieldErrors = validateCustomField(
                                customField.key,
                                customField.value
                              );
                              const hasErrors = fieldErrors.length > 0;

                              return (
                                <div
                                  key={index}
                                  className={`border rounded p-3 mb-3 ${
                                    hasErrors
                                      ? "border-danger"
                                      : "border-secondary"
                                  }`}
                                >
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h6 className="mb-0">
                                      Campo Personalizado #{index + 1}
                                    </h6>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() =>
                                        removeCustomField(func.id, index)
                                      }
                                    >
                                      <IconTrash size={14} />
                                    </Button>
                                  </div>

                                  <Row>
                                    <Col md={4}>
                                      <Form.Group className="mb-2">
                                        <Form.Label>Chave *</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={customField.key || ""}
                                          onChange={(e) =>
                                            updateCustomField(
                                              func.id,
                                              index,
                                              "key",
                                              e.target.value
                                            )
                                          }
                                          placeholder="nome_campo"
                                          isInvalid={
                                            hasErrors &&
                                            (!customField.key ||
                                              !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(
                                                customField.key
                                              ))
                                          }
                                        />
                                        <Form.Text className="text-muted">
                                          Use apenas letras, números e _
                                        </Form.Text>
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group className="mb-2">
                                        <Form.Label>Descrição *</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={customField.value || ""}
                                          onChange={(e) =>
                                            updateCustomField(
                                              func.id,
                                              index,
                                              "value",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Descrição do que o campo representa"
                                          isInvalid={
                                            hasErrors &&
                                            (!customField.value ||
                                              customField.value.trim() === "")
                                          }
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                      <Form.Group className="mb-2">
                                        <Form.Label>Obrigatório</Form.Label>
                                        <Form.Check
                                          type="checkbox"
                                          checked={
                                            customField.required || false
                                          }
                                          onChange={(e) =>
                                            updateCustomField(
                                              func.id,
                                              index,
                                              "required",
                                              e.target.checked
                                            )
                                          }
                                        />
                                      </Form.Group>
                                    </Col>
                                  </Row>

                                  {hasErrors && (
                                    <Alert
                                      variant="danger"
                                      className="mb-0 mt-2"
                                    >
                                      <small>
                                        <strong>Erros:</strong>
                                        <ul className="mb-0 mt-1">
                                          {fieldErrors.map(
                                            (error, errorIndex) => (
                                              <li key={errorIndex}>{error}</li>
                                            )
                                          )}
                                        </ul>
                                      </small>
                                    </Alert>
                                  )}
                                </div>
                              );
                            })}

                            {(!watch(
                              `functionSchemas.${func.id}.customFields`
                            ) ||
                              watch(`functionSchemas.${func.id}.customFields`)
                                .length === 0) && (
                              <div className="text-center py-3 text-muted">
                                <IconSettings size={32} className="mb-2" />
                                <p className="mb-0">
                                  Nenhum campo personalizado adicionado.
                                </p>
                                <small>
                                  Clique em "Adicionar Campo" para criar campos
                                  de metadata personalizados.
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                        {/* {func.registerData && ( */}
                        <div className="mt-4 pt-3 border-top">
                          <Form.Check
                            type="switch"
                            id={`useDefault-${func.id}`}
                            label="Cadastrar no CRM"
                            className="mb-3 ml-3"
                            {...register(
                              `functionSchemas.${func.id}.registerContactData`
                            )}
                          />
                          {/* )} */}
                        </div>
                        <div className="mt-4 pt-3 border-top">
                          <Form.Check
                            type="switch"
                            id={`useDefault-${func.id}`}
                            label="Cadastrar Cliente"
                            className="mb-3 ml-3"
                            {...register(
                              `functionSchemas.${func.id}.registerClientData`
                            )}
                          />
                          {/* )} */}
                        </div>

                        {/* Resposta Personalizada */}
                        {func.hasResponse && (
                          <div className="mt-4 pt-3 border-top">
                            <h6 className="mb-3">💬 Resposta do Agente</h6>

                            <Form.Check
                              type="switch"
                              id={`useDefault-${func.id}`}
                              label="Usar resposta automática do sistema"
                              className="mb-3 ml-3"
                              {...register(
                                `functionSchemas.${func.id}.useDefault`
                              )}
                            />

                            {!watch(
                              `functionSchemas.${func.id}.useDefault`
                            ) && (
                              <Form.Group>
                                <Form.Label>
                                  Resposta Personalizada
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="ms-2"
                                    onClick={() =>
                                      handleAIFill(
                                        `functionSchemas.${func.id}.customResponse`,
                                        {
                                          label: `Resposta para ${func.name}`,
                                          description: `Resposta que o agente dará após executar ${func.name.toLowerCase()}`,
                                        }
                                      )
                                    }
                                  >
                                    <IconWand className="me-1" size={14} />
                                    IA
                                  </Button>
                                </Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  {...register(
                                    `functionSchemas.${func.id}.customResponse`
                                  )}
                                  placeholder={`O que o agente deve responder após ${func.name.toLowerCase()}?`}
                                />
                                <Form.Text className="text-muted">
                                  💡 Use códigos como {"{{nome}}"}, {"{{data}}"}
                                  , {"{{hora}}"} para dados dinâmicos
                                </Form.Text>
                              </Form.Group>
                            )}

                            {watch(`functionSchemas.${func.id}.useDefault`) && (
                              <Alert variant="success" className="mb-0">
                                <small>
                                  ✅ O sistema criará automaticamente uma
                                  resposta profissional com os dados
                                  preenchidos!
                                </small>
                              </Alert>
                            )}
                          </div>
                        )}

                        {!func.hasResponse && (
                          <Alert variant="info" className="mt-3">
                            <small>
                              🔧 Esta é uma função interna - retorna uma
                              resposta estruturada do sistema.
                            </small>
                          </Alert>
                        )}
                      </div>
                    )}

                    {!watch(`functionSchemas.${func.id}.enabled`) && (
                      <div className="text-center py-4 text-muted">
                        <IconSettings size={32} className="mb-2" />
                        <p className="mb-0">
                          Ative esta função para configurar seus parâmetros
                        </p>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>

            {/* Resumo das Funções Ativas */}
            {AVAILABLE_FUNCTIONS.some((func) =>
              watch(`functionSchemas.${func.id}.enabled`)
            ) && (
              <Alert variant="primary" className="mt-4">
                <h6>📊 Resumo das Suas Funções</h6>
                <Row>
                  {AVAILABLE_FUNCTIONS.filter((func) =>
                    watch(`functionSchemas.${func.id}.enabled`)
                  ).map((func) => {
                    const enabledFields = func.fields.filter((field) =>
                      watch(
                        `functionSchemas.${func.id}.fields.${field.id}.enabled`
                      )
                    );
                    const requiredFields = enabledFields.filter((field) =>
                      watch(
                        `functionSchemas.${func.id}.fields.${field.id}.required`
                      )
                    );
                    const customFields =
                      watch(`functionSchemas.${func.id}.customFields`) || [];
                    const validCustomFields = customFields.filter(
                      (cf) => cf.key && cf.value
                    );

                    return (
                      <Col md={6} key={func.id} className="mb-2">
                        <div className="d-flex align-items-center">
                          <span className="me-2">{func.icon}</span>
                          <div>
                            <strong>{func.name}</strong>
                            <br />
                            <small className="text-muted">
                              {enabledFields.length} campos padrão •{" "}
                              {validCustomFields.length} personalizados
                              <br />
                              {requiredFields.length +
                                validCustomFields.filter((cf) => cf.required)
                                  .length}{" "}
                              obrigatórios
                            </small>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Alert>
            )}
          </div>
        )}

        {!watch("functionsEnabled") && (
          <Alert variant="secondary">
            <div className="text-center py-4">
              <IconFunction size={48} className="mb-3 text-muted" />
              <h6>Funções Desabilitadas</h6>
              <p className="mb-0">
                Seu agente funcionará apenas com conversas de texto.
                <br />
                <small className="text-muted">
                  Ative as funções para liberar o poder da automação! 🚀
                </small>
              </p>
            </div>
          </Alert>
        )}
      </div>
    );
  }
);

StepFunctions.displayName = "StepFunctions";

export default StepFunctions;
